import { useWizard } from '@zillow/zrs-rental-applications';
import { Field, Form as ReactFinalForm } from 'react-final-form';
import React, { useCallback } from 'react';
import {
  DetailedIconRent,
  Form,
  FormField,
  FormHelp,
  Input,
  Label,
  Select,
  VisuallyHidden,
} from '@zillow/constellation';
import { getWizardOnSubmit } from '../../../../utils/wizardUtils';
import { formatListing, useAddressDecorator } from './EnterYourAddressStep.utils';
import useCreateProperty from '../../../../utils/apis/useCreateProperty';
import { useHistory, useLocation } from 'react-router-dom';
import { ZTSS_ENTER_YOUR_NAME, ZTSS_INVITE_A_RENTER } from '../../../../constants/paths';
import LinkButton from '../../../../components/LinkButton';
import useGetProperties from '../../../../utils/apis/useGetProperties';
import AddressField from './AddressField';
import ListingWizardStep from '../ListingWizardStep';

function InternalFormRenderer({
  form,
  values: { propertyTypeCode, listingAlias },
  invalid,
  previouslyEnteredListings,
  id,
  label,
}) {
  const { isLoading } = useWizard();
  const { state: { isFromNameStep = false } = {} } = useLocation();
  const renderNextButton = useCallback(
    ({ onRequestNext, buttonProps }) => (
      <LinkButton
        to={listingAlias ? `${ZTSS_INVITE_A_RENTER}?propertyId=${listingAlias}` : ZTSS_INVITE_A_RENTER}
        disabled={invalid || isLoading}
        {...buttonProps}
        onClick={(e) => {
          e.preventDefault();
          onRequestNext();
        }}
      >
        Continue
      </LinkButton>
    ),
    [invalid, isLoading, listingAlias],
  );
  const renderPrevButton = useCallback(
    ({ buttonProps }) => {
      return isFromNameStep ? (
        <LinkButton
          to={{ pathname: ZTSS_ENTER_YOUR_NAME, state: { isFromNameStep: true } }}
          disabled={isLoading}
          {...buttonProps}
        >
          Back
        </LinkButton>
      ) : (
        <div />
      );
    },
    [isFromNameStep, isLoading],
  );
  return (
    <ListingWizardStep
      id={id}
      label={label}
      onSubmit={getWizardOnSubmit(form)}
      meta={{
        renderNextButton,
        renderPrevButton,
      }}
      icon={<DetailedIconRent />}
      title="What's your property's address?"
      description="Add the details for the property where you're screening applications."
      render={({ handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Field
            name="propertyTypeCode"
            parse={(value) => Number.parseInt(value, 10)}
            format={(value) => value.toString()}
            render={({ input }) => {
              return (
                <FormField
                  label={<Label>Property type</Label>}
                  control={
                    <Select {...input}>
                      <option value="6">House</option>
                      <option value="5">Apartment/condo</option>
                      <option value="7">Townhouse</option>
                    </Select>
                  }
                  disabled={listingAlias}
                  marginBottom="md"
                />
              );
            }}
          />
          <AddressField previouslyEnteredListings={previouslyEnteredListings} marginBottom="md" />
          {propertyTypeCode !== 6 && (
            <Field
              name="unitNumber"
              render={({ input }) => {
                return (
                  <FormField
                    label={<Label>Unit number</Label>}
                    control={<Input {...input} />}
                    description={
                      <FormHelp>If the property doesn’t have a unit number, leave this field blank</FormHelp>
                    }
                    disabled={listingAlias}
                    marginBottom="md"
                  />
                );
              }}
            />
          )}
          <VisuallyHidden as="button" type="submit" id="submit-address-form">
            Submit
          </VisuallyHidden>
        </Form>
      )}
    />
  );
}

export default function EnterYourAddressStep({ id, label }) {
  const history = useHistory();
  const { data: previouslyEnteredListings = [] } = useGetProperties({
    select(responseData) {
      return responseData.data.response.listings.map(formatListing);
    },
  });
  const addressDecorator = useAddressDecorator(previouslyEnteredListings);
  const { mutateAsync: createProperty } = useCreateProperty({
    async onError(error) {
      if (error?.response?.data?.error?.data?.duplicateListingAlias) {
        const { duplicateListingAlias: listingAlias } = error.response.data.error.data;
        history.push(`${ZTSS_INVITE_A_RENTER}?propertyId=${listingAlias}`);
      }
    },
    async onSuccess(responseData) {
      const { listingAlias } = responseData.data.response;
      history.push(`${ZTSS_INVITE_A_RENTER}?propertyId=${listingAlias}`);
    },
  });
  return (
    <ReactFinalForm
      initialValues={{
        // not modifiable by the user
        listingTypeCode: 1,
        fullAddress: '',
        listingAlias: null,
        propertyTypeCode: 6,
        unitNumber: null,
      }}
      decorators={[addressDecorator]}
      onSubmit={async (values) => {
        if (values.listingAlias) {
          history.push(`${ZTSS_INVITE_A_RENTER}?propertyId=${values.listingAlias}`);
          return;
        }
        await createProperty(values);
      }}
      component={InternalFormRenderer}
      id={id}
      label={label}
      previouslyEnteredListings={previouslyEnteredListings}
    />
  );
}
